import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack, Title, Stepper, Button, Group, TextInput,
  Textarea, Select, NumberInput, Paper, Text, Progress,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconArrowRight, IconSend, IconDeviceFloppy } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useCurrentTenant } from '@/store/auth'
import { FreezePeriodsWarning } from '@/components/chamados'

interface Category { id: number; name: string }
interface FreezePeriod { id: number; name: string; starts_at: string; ends_at: string }

export function GmudForm() {
  const navigate = useNavigate()
  const tenantId = useCurrentTenant()?.id
  const queryClient = useQueryClient()
  const [active, setActive] = useState(0)
  const [draftId, setDraftId] = useState<number | null>(null)

  // Step 1: Identificacao
  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [classification, setClassification] = useState('normal')
  const [riskLevel, setRiskLevel] = useState('medium')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [priority, setPriority] = useState('medium')

  // Step 2: Planejamento
  const [scheduledStart, setScheduledStart] = useState('')
  const [scheduledEnd, setScheduledEnd] = useState('')
  const [affectedSystems, setAffectedSystems] = useState('')
  const [communicationPlan, setCommunicationPlan] = useState('')
  const [estimatedUsers, setEstimatedUsers] = useState<number | string>('')

  // Step 3: Rollback
  const [rollbackPlan, setRollbackPlan] = useState('')
  const [rollbackPlanError, setRollbackPlanError] = useState<string | null>(null)
  const [rollbackTime, setRollbackTime] = useState<number | string>('')
  const [rollbackCriteria, setRollbackCriteria] = useState('')

  // Step 4: Evidências
  const [testEvidence, setTestEvidence] = useState('')
  const [cabMeetingDate, setCabMeetingDate] = useState('')

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['ticket-categories', tenantId],
    queryFn: async () => (await api.get<Category[]>(`/v1/tenants/${tenantId}/ticket-categories`)).data,
    enabled: !!tenantId,
  })

  const { data: freezePeriods = [] } = useQuery<FreezePeriod[]>({
    queryKey: ['freeze-periods', tenantId],
    queryFn: async () => {
      const res = await api.get<FreezePeriod[]>(`/v1/tenants/${tenantId}/gmud/freeze-periods`)
      return res.data
    },
    enabled: !!tenantId,
  })

  // Verificar conflito de freeze
  const freezeConflict = scheduledStart
    ? freezePeriods.find((fp) => {
        const s = new Date(scheduledStart).getTime()
        return s >= new Date(fp.starts_at).getTime() && s <= new Date(fp.ends_at).getTime()
      }) ?? null
    : null

  const buildPayload = () => ({
    title: title.trim(),
    description: description.trim(),
    gmud_classification: classification,
    risk_level: riskLevel,
    priority,
    category_id: categoryId ? Number(categoryId) : undefined,
    scheduled_start: scheduledStart || undefined,
    scheduled_end: scheduledEnd || undefined,
    affected_systems: affectedSystems ? affectedSystems.split('\n').filter(Boolean) : [],
    communication_plan: communicationPlan || undefined,
    estimated_users_impacted: estimatedUsers ? Number(estimatedUsers) : undefined,
    rollback_plan: rollbackPlan || undefined,
    rollback_time_minutes: rollbackTime ? Number(rollbackTime) : undefined,
    rollback_trigger_criteria: rollbackCriteria || undefined,
    test_evidence: testEvidence || undefined,
    cab_meeting_date: cabMeetingDate || undefined,
  })

  const saveDraft = useMutation({
    mutationFn: async () => {
      const payload = buildPayload()
      if (draftId) {
        await api.patch(`/v1/tenants/${tenantId}/gmud/${draftId}`, payload)
        return draftId
      } else {
        const res = await api.post<{ id: number }>(`/v1/tenants/${tenantId}/gmud`, payload)
        return res.data.id
      }
    },
    onSuccess: (id) => {
      setDraftId(id)
      notifications.show({ color: 'blue', message: 'Rascunho salvo.' })
    },
    onError: () => {
      notifications.show({ color: 'red', message: 'Erro ao salvar rascunho.' })
    },
  })

  const submit = useMutation({
    mutationFn: async () => {
      let id = draftId
      if (!id) {
        const res = await api.post<{ id: number }>(`/v1/tenants/${tenantId}/gmud`, buildPayload())
        id = res.data.id
        setDraftId(id)
      }
      await api.post(`/v1/tenants/${tenantId}/gmud/${id}/submit`)
      return id
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['gmud-list', tenantId] })
      notifications.show({ color: 'green', message: 'GMUD submetida com sucesso!' })
      navigate(`/gmud/${id}`)
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro: ${err.message}` })
    },
  })

  const validateStep = () => {
    if (active === 0 && !title.trim()) {
      setTitleError('Título e obrigatório.')
      return false
    }
    if (active === 0) setTitleError(null)
    if (active === 2 && !rollbackPlan.trim()) {
      setRollbackPlanError('Plano de rollback e obrigatório.')
      return false
    }
    if (active === 2) setRollbackPlanError(null)
    return true
  }

  const steps = ['Identificação', 'Planejamento', 'Rollback', 'Evidências']

  return (
    <Stack gap="lg" maw={800} mx="auto">
      <Group>
        <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate('/gmud')} aria-label="Voltar para lista de GMUDs">
          Voltar
        </Button>
        <Title order={3}>Nova GMUD</Title>
        <Text size="xs" c="dimmed" ml="auto">
          {draftId ? `Rascunho #${draftId}` : 'Novo rascunho'}
        </Text>
      </Group>

      <Progress value={(active / (steps.length - 1)) * 100} size="xs" color="blue" role="status" aria-label={`Etapa ${active + 1} de ${steps.length}`} />

      <Stepper active={active} onStepClick={setActive} size="sm">
        {steps.map((label) => <Stepper.Step key={label} label={label} />)}
      </Stepper>

      <Paper shadow="xs" p="lg" withBorder>
        {active === 0 && (
          <Stack gap="md">
            <TextInput label="Título" required value={title} onChange={(e) => { setTitle(e.target.value); if (titleError) setTitleError(null) }} error={titleError} />
            <Textarea label="Descrição" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
            <Group grow>
              <Select
                label="Classificação" value={classification} onChange={(v) => setClassification(v ?? 'normal')}
                data={[
                  { value: 'standard', label: 'Standard — pré-aprovada, sem CCM' },
                  { value: 'normal', label: 'Normal — fluxo CCM completo' },
                  { value: 'urgent', label: 'Urgente — CCM acelerado' },
                  { value: 'emergency', label: 'Emergencial — execução imediata' },
                ]}
              />
              <Select
                label="Nível de Risco" value={riskLevel} onChange={(v) => setRiskLevel(v ?? 'medium')}
                data={[
                  { value: 'low', label: 'Baixo' },
                  { value: 'medium', label: 'Médio' },
                  { value: 'high', label: 'Alto' },
                  { value: 'very_high', label: 'Muito Alto' },
                ]}
              />
            </Group>
            <Group grow>
              <Select
                label="Categoria" clearable searchable
                data={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                value={categoryId} onChange={setCategoryId}
              />
              <Select
                label="Prioridade" value={priority} onChange={(v) => setPriority(v ?? 'medium')}
                data={[
                  { value: 'low', label: 'Baixa' },
                  { value: 'medium', label: 'Média' },
                  { value: 'high', label: 'Alta' },
                  { value: 'critical', label: 'Crítica' },
                ]}
              />
            </Group>
          </Stack>
        )}

        {active === 1 && (
          <Stack gap="md">
            {classification !== 'emergency' && (
              <>
                <Group grow>
                  <TextInput
                    label="Início da janela (ISO 8601)" placeholder="2026-07-01T02:00:00Z"
                    value={scheduledStart} onChange={(e) => setScheduledStart(e.target.value)}
                  />
                  <TextInput
                    label="Fim da janela (ISO 8601)" placeholder="2026-07-01T04:00:00Z"
                    value={scheduledEnd} onChange={(e) => setScheduledEnd(e.target.value)}
                  />
                </Group>
                <FreezePeriodsWarning freeze={freezeConflict ?? null} />
              </>
            )}
            <Textarea
              label="Sistemas afetados (um por linha)"
              rows={3}
              value={affectedSystems}
              onChange={(e) => setAffectedSystems(e.target.value)}
              placeholder="ec2-web-01&#10;rds-prod&#10;api-gateway"
            />
            <NumberInput
              label="Usuários impactados (estimativa)"
              value={estimatedUsers} onChange={setEstimatedUsers} min={0}
            />
            <Textarea
              label="Plano de comunicação"
              rows={2}
              value={communicationPlan}
              onChange={(e) => setCommunicationPlan(e.target.value)}
              placeholder="Notificar usuários via email 1h antes..."
            />
            {classification === 'normal' && (
              <TextInput
                label="Data da reunião CCM (ISO 8601)"
                placeholder="2026-06-28T10:00:00Z"
                value={cabMeetingDate} onChange={(e) => setCabMeetingDate(e.target.value)}
              />
            )}
          </Stack>
        )}

        {active === 2 && (
          <Stack gap="md">
            <Textarea
              label="Plano de Rollback" required rows={5}
              value={rollbackPlan} onChange={(e) => { setRollbackPlan(e.target.value); if (rollbackPlanError) setRollbackPlanError(null) }}
              error={rollbackPlanError}
              placeholder="Passos para reverter a mudança..."
            />
            <Group grow>
              <NumberInput
                label="Tempo estimado de rollback (minutos)"
                value={rollbackTime} onChange={setRollbackTime} min={0}
              />
            </Group>
            <Textarea
              label="Critério para acionar rollback"
              rows={2}
              value={rollbackCriteria}
              onChange={(e) => setRollbackCriteria(e.target.value)}
              placeholder="Se CPU > 90% por mais de 5min após a mudança..."
            />
          </Stack>
        )}

        {active === 3 && (
          <Stack gap="md">
            <Textarea
              label="Evidências de testes realizados"
              rows={5}
              value={testEvidence}
              onChange={(e) => setTestEvidence(e.target.value)}
              placeholder="Descreva os testes realizados e seus resultados..."
            />
          </Stack>
        )}
      </Paper>

      <Group justify="space-between">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => setActive((a) => Math.max(0, a - 1))}
            disabled={active === 0}
          >
            Anterior
          </Button>
          <Button
            variant="light"
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={() => saveDraft.mutate()}
            loading={saveDraft.isPending}
          >
            Salvar Rascunho
          </Button>
        </Group>
        <Group>
          {active < steps.length - 1 ? (
            <Button
              rightSection={<IconArrowRight size={14} />}
              onClick={() => { if (validateStep()) setActive((a) => a + 1) }}
            >
              Próximo
            </Button>
          ) : (
            <Button
              leftSection={<IconSend size={14} />}
              onClick={() => submit.mutate()}
              loading={submit.isPending}
              color="green"
            >
              Submeter GMUD
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  )
}
